import { int32Error } from '~shared/lib/zod';

import {
  emailSchema,
  getMobilePhonePhSchema,
} from '@breezeventures/web-ph-validators/zod-schemas';
import { z } from 'zod';

const MAX_TEXT_LENGTH = 100;

const preprocessNumberField = <
  T extends z.ZodInt32 | z.ZodOptional<z.ZodInt32>,
>(
  schema: T
) =>
  z.preprocess(
    (val) =>
      val === '' ||
      val === null ||
      (typeof val === 'string' && val.trim() === '')
        ? undefined
        : typeof val === 'string'
          ? Number(val)
          : val,
    schema
  );

const baseStoreSchema = z.object({
  uiId: z.string(),
  name: z
    .string()
    .trim()
    .min(1, 'Store name is required')
    .max(
      MAX_TEXT_LENGTH,
      `Store name must not exceed ${MAX_TEXT_LENGTH} characters`
    ),
  email: emailSchema,
  contactNumber: getMobilePhonePhSchema({
    required: true,
    requiredMessage: 'Contact number is required',
  }),
  avgMonthlySales: preprocessNumberField(
    z.int32(int32Error('Enter a valid value')).positive('Enter a valid value')
  ),
});

const onlineStoreSchema = baseStoreSchema.extend({
  type: z.literal('ONLINE'),
  websiteURL: z
    .string()
    .trim()
    .min(1, 'Website or social media page is required')
    .max(MAX_TEXT_LENGTH, `URL must not exceed ${MAX_TEXT_LENGTH} characters`)
    .pipe(z.url('Enter valid URL')),
});

const offlineStoreSchema = baseStoreSchema
  .extend({
    type: z.literal('OFFLINE'),
    websiteURL: z
      .string()
      .max(100, 'URL must not exceed 100 characters')
      .pipe(z.url('Enter valid URL'))
      .optional()
      .or(z.literal('')),
    tinCode: z
      .string()
      .trim()
      .min(1, 'TIN is required')
      .regex(/^\d{9,13}$/, 'TIN must have 9-13 digits'),
    areaSqm: preprocessNumberField(
      z
        .int32(int32Error('Store size must be an integer'))
        .positive('Store size must be positive')
    ),
    numberOfFloors: preprocessNumberField(
      z
        .int32(int32Error('Number of floors must be an integer'))
        .min(1, 'Number of floors must be at least 1')
    ),
    hasCCTV: z.boolean(),
    dayOfRecording: preprocessNumberField(
      z
        .int32(int32Error('Days number must be an integer'))
        .min(1, 'Days number must be positive')
        .optional()
    ),
  })
  .refine((data) => !data.hasCCTV || data.dayOfRecording !== undefined, {
    path: ['dayOfRecording'],
    message: 'Days of recording is required',
  });

export const storeDetailsSchema = z.discriminatedUnion('type', [
  onlineStoreSchema,
  offlineStoreSchema,
]);

export type StoreDetailsFormInput = z.input<typeof storeDetailsSchema>;
export type StoreDetailsFormValues = z.output<typeof storeDetailsSchema>;
export type OnlineStoreFormValues = z.infer<typeof onlineStoreSchema>;
export type OfflineStoreFormValues = z.infer<typeof offlineStoreSchema>;
